import {
    faCog,
    faToggleOff,
    faToggleOn,
} from '@fortawesome/fontawesome-free-solid';
import { equals } from 'ramda';
import React, { CSSProperties as css, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import AlertsIcon from '../../assets/svg/alerts_24px.svg';
import { useIsReadOnly } from '../../hooks/useIsReadOnly';
import C from '../../shared/constants';
import * as alertsdb from '../../shared/db/alerts-db';
import { getReadOnlyStatus } from '../../shared/db/general-db';
import { withSentry } from '../../shared/hoc/withSentry';
import { iDeviceEvent, iFullStoreState, ItemType, UserAuth } from '../../shared/interfaces';
import { localStorage } from '../../shared/storage';
import Dialog, { DialogConfigSetter } from '../Dialog';
import { Fa } from '../elements/fa';
import LocalPersonTile from '../pages/PersonTabPage/LocalPeopleTile';

interface iProps {
    eventKeys: string[];
    values: { [alertType: string]: { config?: string; on: boolean } } | 'initializing';
    itemType: ItemType;
    subLayer?: string;
    itemId: string;
    style?: css;
    broadcast?: { itemType: ItemType; itemId: string }[];
    itemIdProps?: string;
    itemTypeProps?: ItemType;
    deviceId?: string;
    property?: string;
    isPinned?: boolean;
    title?: string;
    cssOrder?: number,
    showPin?: boolean
}

export const AlertToggles = withSentry((props: iProps) => {
    const authUser = useSelector<iFullStoreState, UserAuth>((s) => s.auth.user!, (l, r) => equals(l, r));
    const events = useSelector<iFullStoreState, Array<iDeviceEvent>>(
        // @ts-ignore
        (s) => props.eventKeys.map((k) => s.general.deviceEvents?.[k]).filter(Boolean),
        (l, r) => equals(l, r)
    );

    const [collapsed, setCollapsed] = useState<boolean>(() => {
        return props.property ? !props.isPinned : true;
    });


    const on = (type: string) => props.values === 'initializing' ?
        false :
        !!((props.values || {})[type] || {} as any).on;

    const toggleOn = async (alertType: string, on: boolean) => {

        const { itemType, itemId, subLayer, broadcast = [] } = props;
        const extra = broadcast.map((x) => ({ ...x, alertType }));

        alertsdb.toggleAlerts(authUser)([{ itemType, itemId, alertType }, ...extra], on, subLayer);
    };

    const { itemType, itemId, itemIdProps, itemTypeProps, deviceId, property, isPinned } = props;


    const [readOnly, setReadOnly] = useState(false);

    useEffect(() => {
        getReadOnlyStatus(authUser.uid, itemId).then((res) => setReadOnly(res));
    }, []);

    useEffect(() => {
        const initialCollapsed = localStorage.get('fromAlertSettings', true)

        localStorage.forget('fromAlertSettings');

        setCollapsed(initialCollapsed);
    }, []);

    const isReadOnly = useIsReadOnly(itemIdProps, itemTypeProps)

    const dialogRef = React.useRef<DialogConfigSetter>();
    const setupDialog = (callBack: () => DialogConfigSetter) => dialogRef.current = callBack();

    const switcher = (event) => {
        if (readOnly) {
            const dialog = dialogRef.current;

            dialog?.({
                type: 'NOTIFICATION',
                title: 'Permissions ERROR!',
                body: 'You do not have permission to change these settings.',
            });
        } else {
            toggleOn(event.key, !on(event.key));
        }
    };

    return (
        <LocalPersonTile
            collapsible
            initialCollapsed={collapsed}
            title={<span style={{ fontSize: 11 }}>{props.title || 'Alerts'}</span>}
            action={null}
            isPinned={isPinned}
            withPin={props.showPin}
            property={property}
            order={props.cssOrder}
            leftIcon={null}
        >
            {events.length > 0 ? (<div>
                <div style={{ display: 'flex' }}>
                    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', fontSize: 11 }}>
                        <div style={colCss}>
                            {events.map((event, idx) => (
                                <div key={event.key} style={rowCss(idx)}>
                                    {event.name}
                                    <ConfigLink style={isReadOnly ? { color: "gray", pointerEvents: "none" } : {}}
                                        key={event.name} alertType={event.key} itemType={itemType}
                                        itemId={itemId} />
                                </div>)
                            )}
                        </div>
                    </div>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        borderLeft: `1px solid ${C.lightGray}`,
                        width: '40px'
                    }}>
                        <div style={colCss}>
                            {events
                                .map((event) => <Fa key={event.key}
                                    onClick={() => switcher(event)}
                                    icon={on(event.key) ? faToggleOn : faToggleOff}
                                    style={{
                                        fontSize: 11, ...(!on(event.key) ? { color: "black" } : { color: 'green' }), ...(isReadOnly ? {
                                            color: "gray",
                                            pointerEvents: "none"
                                        } : {})
                                    }}
                                />)
                                .map((c, idx) => <div key={idx} style={{ ...rowCss(idx), textAlign: 'center' }}>{c}</div>)}
                        </div>
                    </div>
                </div>
            </div>) : <span>None Yet</span>}
            <Dialog setupConfig={setupDialog} />
        </LocalPersonTile>
    );
});

const ConfigLink = ({ alertType, itemType, itemId, style = {} }) => <Link style={style}
    to={`/${itemType}/${itemId}/alerts/${alertType}`}>
    <span style={{ fontSize: 11, marginLeft: '2px' }}><Fa icon={faCog} /></span>
</Link>;

const colCss: css = {
    display: 'flex',
    flexDirection: 'column',
};

const rowCss = (idx) => (({
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    minHeight: 18,
    textAlign: 'left',
    backgroundColor: idx % 2 ? C.lightGray : 'initial'
}) as any);
